"use client";

import { useFormStatus } from "react-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  label: string;
  disabled?: boolean;
  /** Destructive actions (account deletion) need the red affordance. Kept as
   *  props on the SHARED button rather than a second component so pending
   *  state stays owned in one place. */
  variant?: React.ComponentProps<typeof Button>["variant"];
  /** The tall full-width shape the auth forms use, where this is the only
   *  control on the row. Set false to inherit Button's own sizing instead:
   *  beside a sibling (a dialog's Cancel), the override made the two buttons
   *  visibly different heights. */
  block?: boolean;
  /** Extra classes, merged after the `block` preset. */
  className?: string;
  /** Leading icon, swapped for the spinner while pending. */
  icon?: React.ReactNode;
};

/** The submit button for a `<form action>`: disabled, with a spinner, while
 *  the action is pending, which is the client half of the double-submit
 *  guard. React and Next queue a second submit instead of dropping it, so an
 *  enabled button re-runs the action. `pending` holds until a redirect's
 *  destination renders. A submit before hydration, a retried lost response,
 *  or a second tab still gets through: that half is server-side idempotency
 *  (roadmap → "Idempotent metered creates"). lib/form-submit-buttons.test.ts
 *  keeps every server-action form on this button. */
export function SubmitButton({
  label,
  disabled,
  variant,
  block = true,
  className,
  icon,
}: Props) {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      className={cn(block && "h-10 w-full gap-2 px-4", className)}
      variant={variant}
      loading={pending}
      disabled={disabled}
    >
      {pending ? null : icon}
      {label}
    </Button>
  );
}
